/** @graphql experimental **/

import gql from 'graphql-tag';
import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PlatformSiteAri } from '@atlassian/ari';

import { useLazyQueryInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller';

import type {
	ShareAndRestrictDialogInviteCapabilitiesQuery as ShareAndRestrictDialogInviteCapabilitiesQueryType,
	ShareAndRestrictDialogInviteCapabilitiesQueryVariables,
} from './__types__/ShareAndRestrictDialogInviteCapabilitiesQuery';

const useInviteCapabilityShareAndRestrictDialogQuery =
	// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
	gql`
		query useInviteCapabilityShareAndRestrictDialogQuery($resourceARI: String!) @experimental {
			experimentalInvitePermissions(resourceARI: $resourceARI) {
				directInviteMode
				directInviteDomains
				invitePendingApprovalMode
			}
		}
	`;

export const useInviteCapability = () => {
	const { cloudId: siteId } = useSessionData();
	const resourceARI = PlatformSiteAri.create({ siteId }).toString();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFetchCapabilitiesCompleted = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'success',
				actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const onFetchCapabilitiesError = useCallback(
		(queryError: ApolloError) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'error',
					actionSubject: 'useInviteCapabilityShareAndRestrictDialogQuery',
					attributes: {
						error: queryError.message,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const [loadInviteCapabilities, { data, loading, error, called }] = useLazyQueryInterop<
		ShareAndRestrictDialogInviteCapabilitiesQueryType,
		ShareAndRestrictDialogInviteCapabilitiesQueryVariables
	>(useInviteCapabilityShareAndRestrictDialogQuery, {
		variables: { resourceARI },
		onCompleted: onFetchCapabilitiesCompleted,
		onError: onFetchCapabilitiesError,
	});

	const canUserInvite = useCallback(
		(emails: string[]) => {
			if (loading || error) {
				return false;
			}

			if (
				data?.experimentalInvitePermissions?.invitePendingApprovalMode === 'ANYONE' ||
				data?.experimentalInvitePermissions?.directInviteMode === 'ANYONE'
			) {
				return true;
			}

			if (
				data?.experimentalInvitePermissions?.directInviteMode === 'DOMAIN_RESTRICTED' &&
				emails.some((email) => {
					const domain = email.split('@')[1];
					return data?.experimentalInvitePermissions?.directInviteDomains?.includes(domain);
				})
			) {
				return true;
			}

			return false;
		},
		[data, loading, error],
	);

	return {
		loadInviteCapabilities,
		canUserInvite,
		called,
	};
};
